// @givmo/app-admin — the match-policy builder (07-1.4; the Match Program page since EP-MNT2).
//
// A parity rebuild of the Flutter `match_settings` PolicyBuilder, moved VERBATIM from the Settings →
// Match Settings tab when the policy got its own page under Giving (EP-MNT2; D-CPO-34, OM-17
// design 5). Two columns:
//   - LEFT  → the editor (per-employee budget · optional aggregate cap · optional custom language ·
//             read-only required language). Header "Build your policy" with an Edit → Save toggle +
//             Cancel; inputs are read-only until Edit is pressed; Save is dirty-gated + money-valid.
//   - RIGHT → the server-rendered "Match Policy" string (gray box + Copy), shown only when non-empty.
//
// ★ The save BODY is built by the SHARED `buildMatchPolicyBody` — byte-identical to the onboarding
// wizard's PUT (money as whole-dollar INTEGERS, unchecked sections forced to 0 / '').
//
// ★ THE TESTIDS KEPT THEIR `settings-match-*` NAMES when the builder left Settings — stale-by-design,
// the trade `dashboard-approvals` took when the queue moved to `/approvals`: the e2e policy spec reads
// `settings-match-budget`, so the move costs it a navigation change and nothing else.
//
// ★ THE EDIT GATE IS THE WRITER'S OWN STRING (EP-MNT2): `company:edit_match_policy`, read from the
// `permissions` list `GET /admin/company/me` carries — the same resolved set the writer's
// `@has_company_permission` checks, so the page cannot offer an edit the server refuses, nor hide one
// it allows. The string is Owner-only in the legacy sets but customizable per admin (the employer
// module's Owner catalog), which is why the role alone stopped being a sound inference. Anyone else
// sees the policy READ-ONLY + a note. Two fallbacks keep the old behaviour where the list cannot
// decide: a read that carries NO `permissions` list falls back to the role (`Owner` edits), and a
// read ERROR FAILS OPEN to the editor (the server 403 is the authoritative backstop — a transient
// read failure must never strand a real editor). A 403 on save is surfaced inline either way.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyQueryKey,
  companiesUpdateCompanyMatchPolicyMutation,
} from '@givmo/api-client/query';
import {
  isGivmoError,
  supportReference,
  type AdminOut,
  type CompanyDetailedOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Checkbox,
  Icon,
  MaskedInput,
  Skeleton,
  Textarea,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Check, Copy, Pencil } from 'lucide-react';
import {
  WHOLE_DOLLAR_MASK,
  formatWholeDollars,
  parseWholeDollars,
} from '../onboarding/onboarding-money';
import { GIVMO_MATCH_CRITERIA } from '../onboarding/onboarding-consts';
import {
  MATCH_BUDGET_GUARD_MESSAGE,
  buildMatchPolicyBody,
  isBudgetValid,
  matchPolicyFormsEqual,
  readMatchPolicyForm,
  readRenderedPolicy,
  type MatchPolicyForm,
} from './match-policy';

const PREFIX = 'settings-match';

const INTRO =
  "Customize your company's donation matching policy by choosing (1) how much to match per " +
  'employee, (2) whether you want an aggregate company-wide limit, and (3) if you want to offer ' +
  'any guidance as to the types of charities the company is willing/unwilling to match. These ' +
  'elements combine to form your Match Policy, which will be visible to your employees on Givmo.';

const CUSTOM_HINT =
  'i.e. Givfi will generally approve match requests to charities that support education, health, ' +
  'science, and disaster relief.';

// Worded by the permission, never the role (EP-MNT2-FIX1): the gate below reads the writer's string,
// so an Owner whose custom set excludes it is read-only too — "Only company Owners can edit…" told
// that Owner something false about themselves.
const READ_ONLY_NOTE = "You don't have permission to edit the match policy.";

/** The ONE string the writer (`PUT /admin/company/match_policy`) checks. */
export const EDIT_MATCH_POLICY_PERMISSION = 'company:edit_match_policy';

/**
 * Whether the caller may EDIT the policy, from their standing read (`GET /admin/company/me`) — see the
 * header for why the string decides rather than the role. A read still in flight is the caller's to
 * render (the section shows a skeleton); a failed read fails OPEN.
 */
export function canEditMatchPolicy(read: {
  isSuccess: boolean;
  data: Pick<AdminOut, 'admin_company_role' | 'permissions'> | undefined;
}): boolean {
  if (!read.isSuccess) return true;
  const permissions = read.data?.permissions;
  if (Array.isArray(permissions)) return permissions.includes(EDIT_MATCH_POLICY_PERMISSION);
  return read.data?.admin_company_role === 'Owner';
}

export interface MatchPolicySectionProps {
  company: CompanyDetailedOut;
}

export function MatchPolicySection({ company }: MatchPolicySectionProps) {
  // The caller's standing read — the SAME query key the app shell mounts, so it is a cache read.
  const roleQuery = useQuery(companiesGetCompanyAdminOptions());

  if (roleQuery.isPending) {
    return (
      <div className="pt-20" {...testIdProp(`${PREFIX}-role-loading`)}>
        <Skeleton className="h-12 w-1/3" />
        <Skeleton className="mt-12 h-12 w-2/3" />
      </div>
    );
  }

  return <PolicyBuilder company={company} readOnly={!canEditMatchPolicy(roleQuery)} />;
}

function PolicyBuilder({ company, readOnly }: { company: CompanyDetailedOut; readOnly: boolean }) {
  const queryClient = useQueryClient();

  const saved = useMemo(() => readMatchPolicyForm(company), [company]);
  const rendered = useMemo(() => readRenderedPolicy(company), [company]);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<MatchPolicyForm>(saved);
  const [error, setError] = useState<string | null>(null);
  const [errorReference, setErrorReference] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Re-prime when the saved policy changes (e.g. after a successful save invalidates the company),
  // but only while not actively editing (don't clobber in-progress edits).
  useEffect(() => {
    if (!editing) setForm(saved);
  }, [saved, editing]);

  const requiredLanguage = rendered.requiredLanguage ?? GIVMO_MATCH_CRITERIA;
  const dirty = !matchPolicyFormsEqual(form, saved);

  const updateMutation = useMutation({
    ...companiesUpdateCompanyMatchPolicyMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companiesGetCompanyQueryKey() });
      toast.success('Match policy saved');
      setEditing(false);
      setError(null);
      setErrorReference(null);
    },
    onError: (err) => {
      // Surfaces a 403 (a Member who slipped through on a role-read error) and any other failure.
      setError(isGivmoError(err) ? err.message : 'Something went wrong. Please try again.');
      setErrorReference(isGivmoError(err) ? supportReference(err) : null);
    },
  });

  const submitting = updateMutation.isPending;
  const inputsDisabled = readOnly || !editing || submitting;

  const startEdit = () => {
    setForm(saved);
    setError(null);
    setErrorReference(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setForm(saved);
    setError(null);
    setErrorReference(null);
    setEditing(false);
  };

  const save = () => {
    setError(null);
    setErrorReference(null);
    // Client zero-budget guard (verbatim). The endpoint requires a positive whole-dollar budget.
    if (!isBudgetValid(form.budget)) {
      setError(MATCH_BUDGET_GUARD_MESSAGE);
      return;
    }
    updateMutation.mutate({ body: buildMatchPolicyBody(form) });
  };

  const copyPolicy = async () => {
    try {
      await navigator.clipboard?.writeText(rendered.matchPolicy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable (no permission / insecure context) — fail quietly.
    }
  };

  const saveEnabled = dirty && isBudgetValid(form.budget) && !submitting;

  return (
    <div className="flex flex-col gap-16 pt-16" {...testIdProp(`${PREFIX}-tab`)}>
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
        {INTRO}
      </p>

      <div className="grid grid-cols-1 gap-24 lg:grid-cols-[minmax(0,1fr)_22rem]">
        {/* ── LEFT: the editor ─────────────────────────────────────────────────────────────────── */}
        <Card>
          <CardBody className="flex flex-col gap-20">
            <div className="flex items-center justify-between gap-16">
              <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
                Build your policy
              </h2>
              {!readOnly && !editing && (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  leadingIcon={<Icon icon={Pencil} size={14} aria-hidden />}
                  onClick={startEdit}
                  testId={`${PREFIX}-edit`}
                >
                  Edit
                </Button>
              )}
            </div>

            {readOnly && (
              <p
                className="font-sans text-s14 font-w400 tracking-normal text-text-tertiary"
                {...testIdProp(`${PREFIX}-member-note`)}
              >
                {READ_ONLY_NOTE}
              </p>
            )}

            {/* 1. Per-employee budget — REQUIRED; the checkbox is always-on + disabled. */}
            <FieldRow label="Calendar year match budget per employee of">
              <div className="flex items-center gap-12">
                <Checkbox
                  appearance="saas"
                  checked
                  disabled
                  aria-label="Per-employee match budget is required"
                  testId={`${PREFIX}-budget-checkbox`}
                />
                <div className="w-[220px] max-w-full">
                  <MaskedInput
                    imaskOptions={WHOLE_DOLLAR_MASK}
                    value={formatWholeDollars(form.budget)}
                    placeholder="$0"
                    disabled={inputsDisabled}
                    onAccept={(masked) =>
                      setForm((f) => ({ ...f, budget: parseWholeDollars(masked) }))
                    }
                    aria-label="Per-employee match budget"
                    testId={`${PREFIX}-budget`}
                  />
                </div>
              </div>
            </FieldRow>

            {/* 2. Aggregate company cap — optional. */}
            <FieldRow label="Up to a total aggregate company donation limit of">
              <div className="flex items-center gap-12">
                <Checkbox
                  appearance="saas"
                  checked={form.annualChecked}
                  disabled={inputsDisabled}
                  onCheckedChange={(checked) => setForm((f) => ({ ...f, annualChecked: checked }))}
                  aria-label="Cap total annual company matching"
                  testId={`${PREFIX}-aggregate-checkbox`}
                />
                <div className="w-[220px] max-w-full">
                  <MaskedInput
                    imaskOptions={WHOLE_DOLLAR_MASK}
                    value={formatWholeDollars(form.annualValue)}
                    placeholder="$0"
                    disabled={inputsDisabled || !form.annualChecked}
                    onAccept={(masked) =>
                      setForm((f) => ({ ...f, annualValue: parseWholeDollars(masked) }))
                    }
                    aria-label="Aggregate company matching cap"
                    testId={`${PREFIX}-aggregate-amount`}
                  />
                </div>
              </div>
            </FieldRow>

            {/* 3. Custom eligible-charities language — optional. */}
            <FieldRow label="Custom language regarding eligible charities for the company to match">
              <div className="flex items-start gap-12">
                <span className="pt-2">
                  <Checkbox
                    appearance="saas"
                    checked={form.customChecked}
                    disabled={inputsDisabled}
                    onCheckedChange={(checked) =>
                      setForm((f) => ({ ...f, customChecked: checked }))
                    }
                    aria-label="Include custom eligible-charity language"
                    testId={`${PREFIX}-custom-checkbox`}
                  />
                </span>
                <Textarea
                  value={form.customValue}
                  disabled={inputsDisabled || !form.customChecked}
                  rows={3}
                  placeholder={CUSTOM_HINT}
                  aria-label="Custom eligible-charity language"
                  onChange={(event) => setForm((f) => ({ ...f, customValue: event.target.value }))}
                  containerClassName="flex-1"
                  testId={`${PREFIX}-custom-message`}
                />
              </div>
            </FieldRow>

            {/* 4. Required language — read-only. */}
            <FieldRow label="Required language regarding eligible charities">
              <textarea
                value={requiredLanguage}
                disabled
                rows={4}
                aria-label="Required language regarding eligible charities"
                className={cn(
                  'w-full resize-none rounded-md border bg-surface-sunken p-12',
                  'font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-tertiary',
                  'border-border-subtle outline-none disabled:cursor-not-allowed',
                )}
                {...testIdProp(`${PREFIX}-required-language`)}
              />
            </FieldRow>

            {error !== null && (
              <div className="flex flex-col gap-4">
                <p
                  className="font-sans text-s14 font-w500 tracking-normal text-danger-700"
                  {...testIdProp(`${PREFIX}-error`)}
                >
                  {error}
                </p>
                {errorReference !== null && (
                  <p
                    className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary"
                    {...testIdProp(`${PREFIX}-error-reference`)}
                  >
                    {errorReference}
                  </p>
                )}
              </div>
            )}
          </CardBody>

          {/* Sticky save bar — only while editing. */}
          {!readOnly && editing && (
            <CardFooter className="justify-end gap-8">
              <Button
                appearance="saas"
                tone="ghost"
                size="md"
                disabled={submitting}
                onClick={cancelEdit}
                testId={`${PREFIX}-cancel`}
              >
                Cancel
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                loading={submitting}
                disabled={!saveEnabled}
                onClick={save}
                testId={`${PREFIX}-save`}
              >
                Save changes
              </Button>
            </CardFooter>
          )}
        </Card>

        {/* ── RIGHT: the server-rendered Match Policy (only when non-empty). Sized to its text from `lg`
            (`self-start`), not stretched to the editor's height, where it left a blank half-card. ── */}
        {rendered.matchPolicy.length > 0 && (
          <Card className="lg:self-start" {...testIdProp(`${PREFIX}-rendered`)}>
            <CardBody className="flex flex-col gap-8">
              <div className="flex items-center justify-between">
                <h3 className="font-sans text-s15 font-w700 tracking-givmo text-text-strong">
                  Match Policy
                </h3>
                <Button
                  appearance="saas"
                  tone="link"
                  size="sm"
                  leadingIcon={<Icon icon={copied ? Check : Copy} size={14} aria-hidden />}
                  onClick={() => void copyPolicy()}
                  testId={`${PREFIX}-copy`}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <div
                className="whitespace-pre-wrap rounded-md border border-border-subtle bg-surface-sunken p-12 font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
                {...testIdProp(`${PREFIX}-rendered-text`)}
              >
                {rendered.matchPolicy}
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-8">
      <span className="font-sans text-s13 font-w600 tracking-normal text-text-secondary">
        {label}
      </span>
      {children}
    </div>
  );
}
